import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
            <Link 
              to="/" 
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
            >
              ← Back to Blip
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 dark:text-gray-400 dark:text-gray-500 text-lg mb-8">
                <strong>Last updated:</strong> {new Date().toLocaleDateString()}
              </p>

              <section className="mb-8">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  Your privacy is important to us. This Privacy Policy explains how Blip handles your information 
                  when you use our location-based travel discovery platform.
                </p>

                <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-400 p-4 mb-6">
                  <p className="text-gray-700 dark:text-gray-300 font-semibold mb-2">
                    ✅ Privacy-First Design
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    The mobile app operates in read-only mode and does <strong>not collect, store, or transmit any personal data</strong> to our servers. 
                    All location processing happens locally on your device.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">1. What We Do NOT Collect</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Unlike many mobile apps, our Android application is designed with privacy in mind:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-6">
                  <li><strong>No Account Required:</strong> The app does not require user registration, login, or authentication</li>
                  <li><strong>No Personal Information:</strong> We do not collect names, email addresses, phone numbers, or any identifying information</li>
                  <li><strong>No Location Tracking:</strong> Your location data is never sent to our servers or stored remotely</li>
                  <li><strong>No Usage Analytics:</strong> We do not track how you use the app, which features you access, or your behavior patterns</li>
                  <li><strong>No Device Fingerprinting:</strong> We do not collect device identifiers, IP addresses, or create user profiles</li>
                  <li><strong>No Third-Party Tracking:</strong> No advertising networks, analytics tools, or tracking SDKs are integrated</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">2. How Location Services Work</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Our app uses your device's location services to provide you with relevant nearby points of interest. 
                  <strong>All location processing happens entirely on your device</strong> - your location coordinates are never transmitted to our servers.
                </p>
                
                <div className="bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 dark:border-primary-400 p-4 mb-4">
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    <strong>Foreground Location Access:</strong> When you're actively using the app, we access your location to:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4">
                    <li>Display your current position on the map</li>
                    <li>Request nearby points of interest from our servers (only POI data is sent, not your location)</li>
                    <li>Calculate distances to help you explore places around you</li>
                    <li>Enable discovery mode for browsing local attractions</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 mt-2 text-sm italic">
                    Technical Detail: The app queries our POI database by region (e.g., "show POIs in New York") without transmitting your exact coordinates.
                  </p>
                </div>
                
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    <strong>Background Location Access (Optional):</strong> If you enable the "Always Explore" feature:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1 ml-4 mb-2">
                    <li>The app monitors your location in the background to detect when you're near interesting places</li>
                    <li>Sends you notifications about nearby points of interest</li>
                    <li>All processing happens on your device - no location data is sent to servers</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 italic mt-2">
                    <strong>Note:</strong> Background location access is completely optional. You can use all core features of the app without enabling this feature. 
                    The app will explicitly request your permission before accessing location in the background, and you can disable it at any time 
                    in your device settings.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">3. Local Data Storage</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  The app stores some preferences locally on your device to remember your settings:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-6">
                  <li><strong>Language Preference:</strong> Your selected display language</li>
                  <li><strong>Feature Toggles:</strong> "Always Explore" mode on/off state, child mode settings</li>
                  <li><strong>Notification History:</strong> Which POI notifications you've already seen (to avoid duplicates)</li>
                  <li><strong>App Settings:</strong> Startup mode preferences, battery optimization choices</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  This data is stored only on your device using Android's SharedPreferences and is never transmitted or backed up to our servers. 
                  Uninstalling the app will completely remove all locally stored data.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">4. What We Receive from Your Device</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  The only data transmitted from the app to our servers is:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-6">
                  <li><strong>POI Requests:</strong> When you browse the map, the app requests points of interest by region (e.g., "POIs in London" or "museums within 5km")</li>
                  <li><strong>Content Requests:</strong> When viewing POI details, the app downloads text, images, and video links for that location</li>
                  <li><strong>Language Selection:</strong> Your chosen language is sent with requests to receive content in the correct language</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  <strong>Important:</strong> We cannot identify individual users from these requests. There are no user IDs, session tokens, 
                  or tracking mechanisms. Each request is anonymous and independent.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">5. Third-Party Services</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  The app integrates with third-party services that may have their own privacy practices:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-6">
                  <li><strong>Google Maps Platform:</strong> For map display and place information. Google may collect data according to their <a href="https://policies.google.com/privacy" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
                  <li><strong>YouTube:</strong> When you view video content, YouTube's app or website opens. Subject to <a href="https://www.youtube.com/t/terms" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a></li>
                  <li><strong>Android Text-to-Speech:</strong> Audio narration uses your device's built-in TTS engine</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  We do not control these third-party services and are not responsible for their privacy practices. We recommend reviewing 
                  their privacy policies to understand how they handle data.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">6. Data Security</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  Since we don't collect personal data, your privacy is protected by design. However, we still implement security measures:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-6">
                  <li><strong>Encrypted Connections:</strong> All communication with our servers uses HTTPS/TLS encryption</li>
                  <li><strong>Minimal Permissions:</strong> The app only requests permissions necessary for core functionality (location for maps, notifications for alerts)</li>
                  <li><strong>No Remote Storage:</strong> Your settings and preferences stay on your device</li>
                  <li><strong>Open Source Backend:</strong> Our POI database and API are transparent and auditable</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">7. Data Sharing and Sale</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  <strong>We do NOT collect, share, or sell any personal data.</strong> Since we don't collect user data, there is nothing to share or sell. 
                  The app is completely free and supported by passion for travel, not by data monetization or advertising.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">8. Your Privacy Rights</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  While we don't collect personal data, you still have full control:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-6">
                  <li><strong>Location Control:</strong> Grant or revoke location permissions in Android settings at any time</li>
                  <li><strong>Data Deletion:</strong> Uninstall the app to remove all local data</li>
                  <li><strong>Notifications:</strong> Disable "Always Explore" mode or revoke notification permissions</li>
                  <li><strong>No Account Deletion Needed:</strong> Since there are no accounts, nothing needs to be deleted from our servers</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">9. Data Retention</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  Since we don't collect or store personal data on our servers, there is no data retention policy for user information. 
                  The only data we maintain is the public POI database (places, descriptions, images) which is available to all users and 
                  not associated with any individual.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">10. Children's Privacy</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  Our app does not collect personal information from anyone, including children under 13. Parents can safely allow children 
                  to use the app to explore local places without privacy concerns. The app includes a "Child Mode" feature that simplifies 
                  the interface for younger users.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">11. International Users & GDPR Compliance</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  Our privacy-first approach means we're compliant with data protection regulations worldwide, including GDPR, CCPA, and others. 
                  Since we don't collect personal data, there are no cross-border data transfers, no consent requirements, and no data processing 
                  concerns. The app works the same way for all users regardless of location.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">12. Changes to Privacy Policy</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  If we make material changes to how the app handles data (such as adding user accounts or analytics), we will update this 
                  Privacy Policy and notify users through the app. Your continued use of the service after such changes constitutes acceptance 
                  of the updated policy. The "Last Updated" date at the top of this page shows when the policy was last modified.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">13. Contact Information</h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                  If you have any questions or concerns about how the app handles data, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Email:</strong> support@blip-platform.com
                  </p>
                </div>
              </section>

              <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
                  Also see our <Link to="/terms" className="text-primary-600 hover:underline">Terms of Service</Link> for more information about using Blip.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
